import type { TaskAttemptResponse } from "@history-sim/api-contracts";
import {
  type EvaluableTask,
  type EvaluationResult,
  evaluateTask,
  type TaskResponse,
} from "@history-sim/domain";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { CONTENT_VERSION, useAppStore } from "../state/appStore";
import { LearnerApiHttpError, LearnerApiProtocolError } from "./learnerApiClient";
import { LearnerTaskAttemptQueuedError } from "./learnerSyncCoordinator";
import {
  getLearnerSyncRuntime,
  isLocalOnlyLearnerSession,
  learnerSyncConfiguration,
} from "./learnerSyncRuntime";

export type TaskEvaluationAuthority = "server" | "local-only" | "practice";

export const TASK_ATTEMPT_RESOLVED_EVENT = "history-sim:task-attempt-resolved";
export const TASK_ATTEMPT_REJECTED_EVENT = "history-sim:task-attempt-rejected";
export const TASK_ATTEMPT_QUEUED_EVENT = "history-sim:task-attempt-queued";

export interface TaskAttemptResolvedEventDetail {
  readonly clientAttemptId: string;
  readonly outcome: TaskAdjudicationOutcome;
}

export interface TaskAttemptRejectedEventDetail {
  readonly clientAttemptId: string;
  readonly reasonCode: string;
}

export interface TaskAdjudicationOutcome {
  readonly evaluation: EvaluationResult;
  readonly authority: TaskEvaluationAuthority;
  readonly attemptId?: string;
  readonly attemptNo?: number;
}

export type TaskAdjudicator = (
  task: EvaluableTask,
  response: TaskResponse,
) => TaskAdjudicationOutcome | Promise<TaskAdjudicationOutcome>;

export class TaskAdjudicationError extends Error {
  readonly code: string;
  readonly userMessage: string;
  readonly pendingAttemptId: string | undefined;

  constructor(code: string, userMessage: string, pendingAttemptId?: string) {
    super(`Task adjudication failed: ${code}`);
    this.name = "TaskAdjudicationError";
    this.code = code;
    this.userMessage = userMessage;
    this.pendingAttemptId = pendingAttemptId;
  }
}

function localOutcome(task: EvaluableTask, response: TaskResponse): TaskAdjudicationOutcome {
  return {
    evaluation: evaluateTask(task, response),
    authority: task.formal ? "local-only" : "practice",
  };
}

export function taskAttemptResponseToOutcome(
  response: TaskAttemptResponse,
): TaskAdjudicationOutcome {
  return {
    authority: "server",
    attemptId: response.attemptId,
    attemptNo: response.attemptNo,
    evaluation: {
      valid: response.valid,
      correct: response.correct,
      score: response.score,
      feedback: response.feedback,
      ...(response.misconceptionCode ? { misconceptionCode: response.misconceptionCode } : {}),
      ...(response.diagnostic ? { diagnostic: response.diagnostic } : {}),
    },
  };
}

function safeAdjudicationError(error: unknown): TaskAdjudicationError {
  if (error instanceof TaskAdjudicationError) return error;
  if (error instanceof LearnerTaskAttemptQueuedError) {
    return new TaskAdjudicationError(
      error.code,
      "网络暂不可用，作答已安全暂存；恢复联网后将自动提交服务端判定，请勿重复作答。",
      error.clientAttemptId,
    );
  }
  if (error instanceof LearnerApiHttpError) {
    if (["TASK_BLOCKED", "TASK_NOT_APPROVED"].includes(error.code)) {
      return new TaskAdjudicationError(
        error.code,
        "专家审核已完成，但本题结论尚未映射为系统通过状态，在线正式判题暂未开放。你仍可返回材料继续研学。",
      );
    }
    if (
      [
        "CONTENT_VERSION_MISMATCH",
        "ASSIGNMENT_CONTEXT_MISMATCH",
        "ASSIGNMENT_CONTENT_VERSION_MISMATCH",
        "ASSIGNMENT_CONTENT_DRIFT",
        "TASK_NOT_IN_ASSIGNMENT",
      ].includes(error.code)
    ) {
      return new TaskAdjudicationError(
        error.code,
        "本次作答与当前课程版本不一致，尚未计入成绩。请返回课程入口刷新后再试。",
      );
    }
    if ([401, 403].includes(error.status)) {
      return new TaskAdjudicationError(
        error.code,
        "学习身份已经失效，本次作答尚未计入成绩。请切换身份后重新加入班级。",
      );
    }
    if (error.status === 429) {
      return new TaskAdjudicationError(
        error.code,
        "提交较为频繁，本次作答尚未计入成绩。请稍候再试。",
      );
    }
    return new TaskAdjudicationError(
      error.code,
      "服务端暂时无法完成判题，本次作答尚未计入成绩。请稍后再试。",
    );
  }
  if (error instanceof LearnerApiProtocolError) {
    const networkFailure = ["NETWORK_UNAVAILABLE", "REQUEST_TIMEOUT", "REQUEST_FAILED"].includes(
      error.code,
    );
    return new TaskAdjudicationError(
      error.code,
      networkFailure
        ? "当前网络不可用，本次作答尚未提交，也未计入成绩。联网后可直接重试。"
        : "判题响应未通过安全校验，本次作答尚未计入成绩。请稍后再试。",
    );
  }
  return new TaskAdjudicationError(
    "TASK_ADJUDICATION_FAILED",
    "暂时无法完成判题，本次作答尚未计入成绩。请稍后再试。",
  );
}

export function taskAttemptRejectionMessage(reasonCode: string): string {
  if (["TASK_BLOCKED", "TASK_NOT_APPROVED"].includes(reasonCode)) {
    return "专家审核已完成，但本题结论尚未映射为系统通过状态，暂存作答未计入正式成绩。";
  }
  if (reasonCode.includes("VERSION") || reasonCode.includes("ASSIGNMENT")) {
    return "课程或作业版本已经变化，暂存作答未计入正式成绩。请刷新课程后重试。";
  }
  return "服务端拒绝了暂存作答，本次未计入正式成绩。请返回材料后重新作答。";
}

const TaskAdjudicationContext = createContext<TaskAdjudicator>(localOutcome);

interface TaskAdjudicationProviderProps {
  readonly children: ReactNode;
  readonly adjudicator?: TaskAdjudicator;
}

export function TaskAdjudicationProvider({ children, adjudicator }: TaskAdjudicationProviderProps) {
  const clientSessionId = useAppStore((state) => state.sessionId);
  const profile = useAppStore((state) => state.profile);

  const runtimeAdjudicator = useCallback<TaskAdjudicator>(
    (task, response) => {
      if (
        !task.formal ||
        !profile ||
        learnerSyncConfiguration.mode === "local-only" ||
        isLocalOnlyLearnerSession(clientSessionId)
      ) {
        return localOutcome(task, response);
      }

      const runtime = getLearnerSyncRuntime();
      if (!runtime) {
        throw new TaskAdjudicationError(
          "TASK_SYNC_RUNTIME_UNAVAILABLE",
          "判题服务尚未就绪，本次作答尚未计入成绩。请稍后再试。",
        );
      }
      return runtime.coordinator
        .submitTaskAttempt(clientSessionId, {
          clientAttemptId: crypto.randomUUID(),
          contentVersion: CONTENT_VERSION,
          taskId: task.id,
          response,
        })
        .then(taskAttemptResponseToOutcome)
        .catch((error: unknown) => {
          throw safeAdjudicationError(error);
        });
    },
    [clientSessionId, profile],
  );

  const value = useMemo(() => adjudicator ?? runtimeAdjudicator, [adjudicator, runtimeAdjudicator]);
  return (
    <TaskAdjudicationContext.Provider value={value}>{children}</TaskAdjudicationContext.Provider>
  );
}

export function useTaskAdjudicator(): TaskAdjudicator {
  return useContext(TaskAdjudicationContext);
}
